import * as SwitchPrimitive from "@radix-ui/react-switch";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Loader2 } from "lucide-react";
import { forwardRef, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { useChrome } from "@/lib/chrome";

/** Class names, with later Tailwind classes overriding earlier ones (w-36 beats w-full). */
export function cn(...v: ClassValue[]) {
  return twMerge(clsx(v));
}

// ── Button ──────────────────────────────────────────────────────────────────
type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline" | "live" | "success";
type Size = "xs" | "sm" | "md" | "icon" | "icon-sm";

const variants: Record<Variant, string> = {
  primary: "bg-accent text-white shadow-sm hover:brightness-110 active:brightness-95",
  secondary: "border border-line bg-panel-2 text-fg hover:border-line-strong hover:bg-panel-3",
  ghost: "text-muted hover:bg-panel-3 hover:text-fg",
  outline: "border border-line-strong text-fg hover:bg-panel-3",
  danger: "bg-down text-white shadow-sm hover:brightness-110 active:brightness-95",
  success: "bg-up text-white shadow-sm hover:brightness-110 active:brightness-95",
  live: "bg-live text-white shadow-sm hover:brightness-110",
};
// a finger needs a bigger target than a mouse: touch screens get taller buttons
const sizes: Record<Size, string> = {
  xs: "h-7 px-2.5 text-[12px] gap-1 rounded-md pointer-coarse:h-8",
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-lg pointer-coarse:h-9",
  md: "h-9 px-4 text-[13.5px] gap-2 rounded-lg pointer-coarse:h-10",
  icon: "size-9 rounded-lg pointer-coarse:size-10",
  "icon-sm": "size-8 rounded-md pointer-coarse:size-9",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  // a plain button, not a form's submit, unless asked: inside a form a Cancel or a
  // tab must not send it
  { variant = "secondary", size = "md", loading, icon, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center font-medium whitespace-nowrap transition-[background,border-color,filter,color,scale] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45",
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" /> : icon}
      {children}
    </button>
  );
});

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("rounded-xl border border-line bg-panel shadow-panel", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, actions, icon, className }: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-h-12 items-center gap-2.5 border-b border-line px-4 py-2.5", className)}>
      {icon && <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-panel-3 text-muted [&_svg]:size-3.5">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-semibold tracking-tight">{title}</div>
        {subtitle && <div className="line-clamp-2 text-[12px] text-muted md:line-clamp-none md:truncate">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  );
}

// ── Badge / status ──────────────────────────────────────────────────────────
export type Tone = "neutral" | "up" | "down" | "warn" | "accent" | "live" | "muted";
const tones: Record<Tone, string> = {
  neutral: "bg-panel-3 text-fg border-line",
  up: "bg-up-soft text-up border-transparent",
  down: "bg-down-soft text-down border-transparent",
  warn: "bg-warn-soft text-warn border-transparent",
  accent: "bg-accent-soft text-accent border-transparent",
  live: "bg-live-soft text-live border-transparent",
  muted: "bg-transparent text-muted border-line",
};

export function Badge({ tone = "neutral", className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return (
    <span className={cn("inline-flex h-[22px] shrink-0 items-center gap-1 rounded-md border px-1.5 text-[11.5px] font-medium whitespace-nowrap", tones[tone], className)}>
      {children}
    </span>
  );
}

const dotTones: Record<Tone, string> = {
  neutral: "bg-faint",
  up: "bg-up",
  down: "bg-down",
  warn: "bg-warn",
  accent: "bg-accent",
  live: "bg-live",
  muted: "bg-faint",
};

export function Dot({ tone = "neutral", pulse, className }: { tone?: Tone; pulse?: boolean; className?: string }) {
  return <span className={cn("inline-block size-2 shrink-0 rounded-full", dotTones[tone], pulse && "pulse-dot", className)} />;
}

// ── Form controls ───────────────────────────────────────────────────────────
// a touch screen's fields are at least 40 px tall, whatever height a dense layout asks for
const field =
  "w-full rounded-lg border border-line bg-panel-2 px-3 text-[13.5px] text-fg placeholder:text-faint transition-[border-color,box-shadow] hover:border-line-strong focus:border-accent focus:ring-3 focus:ring-accent/20 focus:outline-none disabled:opacity-50 pointer-coarse:min-h-10";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(field, "h-9", invalid && "border-down", className)} aria-invalid={invalid || undefined} {...rest} />;
});

/** A number field that keeps what is typed while it is being typed. A plain
 * controlled ``type="number"`` input turns a partial entry ("-", "1e", "")
 * into 0 and writes "0" back, so a negative threshold could not be typed.
 * ``onValue`` gets only finite numbers (``undefined`` once cleared, when
 * ``allowEmpty``). */
export function NumberInput({ value, onValue, int, signed, allowEmpty, onBlur, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: number | null | undefined;
  onValue: (v: number | undefined) => void;
  int?: boolean;
  /** may be negative: a phone's numeric keypad has no minus key, so show the full keyboard */
  signed?: boolean;
  allowEmpty?: boolean;
  invalid?: boolean;
}) {
  const shown = value === null || value === undefined || Number.isNaN(value) ? "" : String(value);
  const [text, setText] = useState(shown);
  useEffect(() => {
    // a change from outside (a reset, a recipe): show it, unless it is what is typed
    setText((t) => (t.trim() !== "" && Number(t) === value ? t : shown));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  const parse = (t: string) => {
    const n = int ? Number.parseInt(t, 10) : Number(t);
    return Number.isFinite(n) && (!int || /^-?\d+$/.test(t.trim())) ? n : undefined;
  };
  return (
    <Input
      type="text"
      inputMode={signed ? "text" : int ? "numeric" : "decimal"}
      autoComplete="off"
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === "") {
          if (allowEmpty) onValue(undefined);
          return;
        }
        const n = parse(t);
        if (n !== undefined) onValue(n);
      }}
      onBlur={(e) => {
        // left empty (where a value is required) or half typed ("-", "1e"): show
        // the value that will be saved again, so the box never shows one thing
        // while the form holds another
        const t = text.trim();
        if (t === "" ? !allowEmpty : parse(t) === undefined) setText(shown);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cn(field, "py-2 leading-relaxed", className)} {...rest} />;
});

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(field, "h-9 cursor-pointer appearance-none bg-[length:12px] bg-[right_0.6rem_center] bg-no-repeat pr-7", className)} style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238d96a8' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")` }} {...rest}>
      {children}
    </select>
  );
}

export function Field({ label, hint, error, children, className, inline }: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  inline?: boolean;
}) {
  // the hint sits outside the <label> so it is not read as part of the field's name
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {/* a phone's fields are 40 px tall, easy to hit with a thumb */}
      <label
        className={cn(inline ? "flex items-center justify-between gap-4" : "flex flex-col gap-1.5", "max-md:[&>input]:h-10 max-md:[&>select]:h-10")}
        onClick={(e) => {
          // a label presses its first control on any click inside it: around a row of
          // buttons (colours, Options or Futures) a tap in a gap must not press the first
          const t = e.target as HTMLElement;
          if (t.closest("button, input, select, textarea, a")) return;
          const box = e.currentTarget;
          if (box.querySelector("button") || box.querySelectorAll("input, select, textarea").length !== 1) e.preventDefault();
        }}
      >
        {label && <span className="text-[12.5px] font-medium text-fg/80">{label}</span>}
        {children}
      </label>
      {error ? <span className="text-[12px] text-down">{error}</span> : hint ? <span className="text-[12px] leading-snug text-muted">{hint}</span> : null}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, label, className }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        // a phone's switch is the size of the system one
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:h-7 pointer-coarse:w-12",
        checked ? "bg-accent" : "bg-line-strong",
        className,
      )}
    >
      <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px] pointer-coarse:size-6 pointer-coarse:data-[state=checked]:translate-x-[20px]" />
    </SwitchPrimitive.Root>
  );
}

export function Segmented<T extends string>({ value, options, onChange, size = "sm", className }: {
  value: T;
  options: { value: T; label: ReactNode; tone?: "live" }[];
  onChange: (v: T) => void;
  size?: "xs" | "sm";
  className?: string;
}) {
  return (
    <div className={cn("inline-flex shrink-0 rounded-lg border border-line bg-panel-2 p-0.5", className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-2.5 font-medium whitespace-nowrap transition-colors",
            size === "xs" ? "h-6 text-[12px] pointer-coarse:h-8 pointer-coarse:px-3" : "h-7 text-[13px] pointer-coarse:h-9 pointer-coarse:px-3",
            value === o.value
              ? o.tone === "live"
                ? "bg-live text-white shadow-sm"
                : "bg-raised text-fg shadow-sm"
              : "text-muted hover:text-fg",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Feedback ────────────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-4 animate-spin text-muted", className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-panel-3", className)} />;
}

export function Empty({ icon, title, children, action, className }: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-12 text-center", className)}>
      {icon && <div className="mb-1.5 grid size-12 place-items-center rounded-2xl border border-line bg-panel-2 text-muted">{icon}</div>}
      <div className="text-[14.5px] font-semibold">{title}</div>
      {children && <div className="max-w-sm text-[13px] leading-relaxed text-muted">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Callout({ tone = "accent", title, children, icon, className }: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] leading-relaxed", tones[tone], className)}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title && "mt-0.5", "text-fg/85")}>{children}</div>}
      </div>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line bg-panel-3 px-1.5 py-0.5 font-mono text-[10.5px] text-muted">{children}</kbd>;
}

export function Section({ title, description, children, actions, className }: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-3", className)}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-[14.5px] font-semibold tracking-tight">{title}</h3>
          {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A page's large title on a phone: the top bar shows the text once the
 * returned ref's element has scrolled under it (the native large-title pattern). */
export function useLargeTitle(text: string | null) {
  const setTitle = useChrome((s) => s.setTitle);
  const setOnScreen = useChrome((s) => s.setTitleOnScreen);
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setTitle(text);
    return () => setTitle(null);
  }, [text, setTitle]);
  useEffect(() => {
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { rootMargin: "-56px 0px 0px 0px" });
    io.observe(el);
    return () => {
      io.disconnect();
      setOnScreen(false);
    };
  }, [el, setOnScreen]);
  return setEl;
}

/** A page's title, description and actions. On a phone the title is large and
 * the top bar picks it up once it scrolls away; the description is clamped and
 * the actions become one row that scrolls sideways. */
export function PageHeader({ title, description, actions, children, leading }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Before the title (a back button). */
  leading?: ReactNode;
}) {
  const ref = useLargeTitle(typeof title === "string" ? title : null);
  return (
    <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 items-start gap-2">
        {leading}
        <div className="min-w-0">
          <h1 ref={ref} className="text-[26px] leading-tight font-bold tracking-tight md:text-[22px] md:font-semibold">
            {title}
          </h1>
          {description && <p className="mt-1 line-clamp-2 max-w-2xl text-[13.5px] leading-relaxed text-muted md:line-clamp-none">{description}</p>}
          {children}
        </div>
      </div>
      {actions && (
        <div className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 sm:-mx-6 sm:px-6 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 lg:justify-end">
          {actions}
        </div>
      )}
    </div>
  );
}
